<template>
  <div>
    <div
      :id="pageId"
      class="page-content"
      :class="pageId"
    >
      <message-html
        :result-html="status.resultHtml"
        :has-top-line="false"
        has-bottom-line
      />

      <div class="row">
        <div class="col-5">
          <div class="card">
            <div class="card-content">
              <div class="col-12 header-section">
                <div class="d-flex justify-content-between align-items-center">
                  <p>Contact List</p>
                  <div class="button-group">
                    <button
                      type="button"
                      class="btn"
                      title="Show checkboxes"
                      @click="showCheckboxes"
                    >
                      <i :class="`fa ${status.isShowCheckbox ? 'fa-check-square-o' : 'fa-square-o'}`" />
                    </button>

                    <button
                      type="button"
                      class="btn"
                      title="Create a new chat room"
                      @click="createRoomEvent"
                    >
                      <i class="fa fa-plus" />
                    </button>

                    <button
                      type="button"
                      class="btn"
                      title="Delete chat room"
                      :disabled="!isContactRoomsSelected"
                      @click="closeRoomEvent"
                    >
                      <i class="fa fa-trash-o" />
                    </button>
                  </div>
                </div>
                <div class="search-box-container">
                  <input
                    v-model="searchQuery"
                    type="text"
                    class="search-box"
                    placeholder="Search messages..."
                  >
                  <i
                    v-if="searchQuery"
                    class="fa fa-times search-clear"
                    title="Clear search"
                    @mousedown.prevent="clearSearch"
                  />
                </div>
              </div>

              <contact-list
                v-if="!searchQuery.trim()"
                ref="contactListRef"
                :selected-contact="selectedContact"
                :selected-contact-ids="selectedContactIds"
                :is-show-checkbox="status.isShowCheckbox"
                @select-room="selectRoom"
                @edit-room="onEditRoom"
                @toggle-select="onToggleSelect"
                @error="status.resultHtml = $event"
              />

              <message-search-list
                v-else
                :search-query="searchQuery"
                :selected-contact="selectedContact"
                @select-room="selectRoom"
                @error="status.resultHtml = $event"
              />
            </div>
          </div>
        </div>

        <div
          v-if="selectedContact"
          class="col-7"
        >
          <chat
            :selected-contact="selectedContact"
            :room-key="roomKey"
            :anchor-message-id="anchorMessageId"
            :anchor-message="anchorMessage"
            @error="status.resultHtml = $event"
            @new-message-added="onNewMessageAdded"
          />
        </div>
      </div>
    </div>

    <create-chat-room-dialog
      v-model="roomPhoneNumber"
      :is-show-dialog="status.isShowCreateRoomDialog"
      @submit="createRoom"
      @cancel="status.isShowCreateRoomDialog = false"
    />

    <edit-contact-info-dialog
      v-model="contactName"
      :is-show-dialog="status.isShowEditContactInfoDialog"
      @submit="updateContactInfo"
      @cancel="status.isShowEditContactInfoDialog = false"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, Ref, reactive, computed, watch, onMounted, defineAsyncComponent } from 'vue';
import { useRoute } from 'vue-router';
import { ElLoading } from 'element-plus';

import util from '@/Utils/util';
import { useCustomer } from '@/use/customer';
import { useSwalDialog } from '@/use/swalDialog';
import { useGlobal } from '@/use/global';
import { ChatRoom, SearchMessage } from '@/models/chatRoom';
import { chatRoomService } from '@/services/chatRoom';

const CreateChatRoomDialog = defineAsyncComponent(() => import('../Dialogs/CreateChatRoom.vue') as any);

const EditContactInfoDialog = defineAsyncComponent(() => import('../Dialogs/EditContactInfo.vue') as any);

const Chat = defineAsyncComponent(() => import('./SMSBoard/Chat.vue') as any);

const ContactList = defineAsyncComponent(() => import('./SMSBoard/ContactList.vue') as any);

const MessageSearchList = defineAsyncComponent(() => import('./SMSBoard/MessageSearchList.vue') as any);

interface ContactListExposed {
  reorderContacts: (pastIndex: number) => void;
  addRoom: (room: ChatRoom) => ChatRoom;
  removeByIds: (ids: string[]) => void;
  updateCallerName: (roomId: string, name: string) => string | null | undefined;
  restoreCallerName: (roomId: string, name: string | undefined) => void;
}

const pageId = 'sms-board';

const route = useRoute();

const { customerPhoneNumber, setCustomerPhoneNumber } = useCustomer();
const { showSuccessPopupDialog, showConfirmPopupDialog } = useSwalDialog();
const { smsBoardState, setNewRoomPhoneNumber } = useGlobal();

const contactListRef = ref<ContactListExposed | null>(null);

const selectedContact: Ref<ChatRoom | null> = ref(null);

const selectedContactIds: Ref<string[]> = ref([]);

const roomPhoneNumber = ref('');
const contactName = ref('');
const roomId = ref('');

const roomKey = ref(0);

// search message input
const searchQuery = ref('');

const clearSearch = () => {
  searchQuery.value = '';
};
// anchor message is when user clicks on a message in the search results
// it is used to load the messages around that anchor message in the chat component.
const anchorMessageId = ref<string | null>(null);
const anchorMessage = ref<SearchMessage | null>(null);

const status = reactive({
  resultHtml: '',
  isShowCreateRoomDialog: false,
  isShowEditContactInfoDialog: false,
  isShowCheckbox: false,
});

const isContactRoomsSelected = computed(() => {
  if (status.isShowCheckbox && selectedContactIds.value.length > 0) {
    return true;
  }

  if (!status.isShowCheckbox && selectedContact.value) {
    return true;
  }

  return false;
});

const onToggleSelect = (roomId: string) => {
  const index = selectedContactIds.value.findIndex((item) => item === roomId);
  if (index === -1) {
    selectedContactIds.value.push(roomId);
  } else {
    selectedContactIds.value.splice(index, 1);
  }
};

const onEditRoom = (room: ChatRoom) => {
  roomId.value = room.id;
  contactName.value = room.callerName ?? '';
  status.isShowEditContactInfoDialog = true;
};

const updateContactInfo = async () => {
  status.isShowEditContactInfoDialog = false;

  const backupName = contactListRef.value?.updateCallerName(roomId.value, contactName.value);
  if (backupName === null) return;

  try {
    await chatRoomService.updateContactName({
      chatRoomId: roomId.value,
      callerName: contactName.value,
    });
  } catch (error) {
    status.resultHtml = util.getGraphqlError(error);
    contactListRef.value?.restoreCallerName(roomId.value, backupName);
  }
};

const selectRoom = (room: ChatRoom, options?: { messageId?: string; anchorMessage?: SearchMessage }) => {
  roomKey.value += 1;
  selectedContact.value = room;
  anchorMessageId.value = options?.messageId ?? null;
  anchorMessage.value = options?.anchorMessage ?? null;
};

const onNewMessageAdded = () => {
  if (selectedContact.value?.index != null) {
    contactListRef.value?.reorderContacts(selectedContact.value.index);
  }
};

const showCheckboxes = () => {
  selectedContactIds.value = [];
  status.isShowCheckbox = !status.isShowCheckbox;
};

const createRoomEvent = () => {
  roomPhoneNumber.value = customerPhoneNumber.value || '';
  status.isShowCreateRoomDialog = true;
};

const createRoom = async () => {
  const loading = ElLoading.service({
    lock: true,
    text: 'Creating Chat Room...',
    background: 'rgba(0, 0, 0, 0.7)',
    customClass: 'custom-full-spinner-class',
  });
  status.resultHtml = '';
  status.isShowCreateRoomDialog = false;

  try {
    const newRoom = await chatRoomService.createChatRoom(roomPhoneNumber.value);
    const addedRoom = contactListRef.value?.addRoom(newRoom);
    selectRoom(addedRoom ?? newRoom);
  } catch (error) {
    status.resultHtml = util.getGraphqlError(error);
  }
  loading.close();
};

const closeRoomEvent = async () => {
  const isCloseContact = await showConfirmPopupDialog({
    text: 'Are you sure you want to remove contact?',
    title: 'Remove contact?',
    confirmButtonText: 'Remove',
  });

  if (!isCloseContact) {
    return;
  }

  closeRooms();
};

const closeRooms = async () => {
  if (!isContactRoomsSelected.value) return;

  const loading = ElLoading.service({
    lock: true,
    text: 'Removing Contact...',
    background: 'rgba(0, 0, 0, 0.7)',
    customClass: 'custom-full-spinner-class',
  });
  status.resultHtml = '';

  const idsToRemove = status.isShowCheckbox
    ? [...selectedContactIds.value]
    : [selectedContact.value?.id!];

  try {
    await chatRoomService.removeChatRoom(idsToRemove);

    loading.close();
    const successMessage = status.isShowCheckbox
      ? `The ${selectedContactIds.value.length} contact(s) has been removed.`
      : 'The contact has been removed.';

    showSuccessPopupDialog({
      title: 'Removed',
      text: successMessage,
    });

    contactListRef.value?.removeByIds(idsToRemove);

    if (status.isShowCheckbox) {
      status.isShowCheckbox = false;

      if (selectedContact.value && !idsToRemove.includes(selectedContact.value.id)) {
        return;
      }

      selectedContact.value = null;
    } else {
      selectedContact.value = null;
    }
  } catch (error) {
    status.resultHtml = util.getGraphqlError(error);
    loading.close();
  }
};

watch(
  () => smsBoardState.newRoomPhoneNumber,
  (value: string) => {
    if (value) {
      roomPhoneNumber.value = value;
      createRoom();
      setNewRoomPhoneNumber('');
    }
  },
);

onMounted(() => {
  util.moveToTop();

  const phoneFromQuery = route.query.phoneNumber as string | undefined;
  if (phoneFromQuery) {
    setCustomerPhoneNumber(phoneFromQuery);
  }
});
</script>

<style lang="scss">
.sms-board {
  &.page-content {
    padding-left: 20px;
    padding-right: 20px;
    button {
      opacity: 1;
      height: 35px;
      width: 35px !important;
      border-radius: 50%;
      padding: 3px 0 0 2px !important;
      font-size: 20px !important;
      border-width: 2px;
      min-width: unset;
      border-color: #353a49;
      background-color: #fff !important;
      color: #353a49 !important;
      &:hover {
        background-color: #fff !important;
        color: #41b883 !important;
        border-color: #41b883;
      }
      &.disabled-button {
        color: gray !important;
        background-color: #c1c1c1 !important;
      }
    }
    .card {
      border-radius: 0;
    }
  }
  p {
    margin-bottom: 0;
  }
  .card-content {
    padding: 0 !important;
  }
  .header-section {
    background: #353a49;
    padding-top: 15px;
    padding-bottom: 10px;
    padding-left: 20px;
    padding-right: 20px;
    p {
      font-size: 24px;
      font-weight: bold;
      color: #fff;
    }
    .search-box-container {
      position: relative;
      margin-top: 10px;
    }
    .search-box {
      width: 100%;
      padding: 8px 32px 8px 12px;
      border: none;
      border-radius: 4px;
      background: #20242d;
      color: #fff;
      font-size: 14px;
      &::placeholder {
        color: #888;
      }
      &:focus {
        outline: none;
      }
    }
    .search-clear {
      position: absolute;
      right: 10px;
      top: 50%;
      transform: translateY(-50%);
      color: #888;
      font-size: 14px;
      cursor: pointer;
      padding: 4px;

      &:hover {
        color: #fff;
      }
    }
  }
  
  
  
  
  .button-group {
    button {
      margin-right: 5px;
    }
    button:last-child {
      margin-right: 0px;
    }
  }
  .hidden {
    display: none;
  }
  
}
</style>
